import { GridCellParams } from '@mui/x-data-grid-pro';
import { Fragment, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { v4 } from 'uuid';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { UpstackDataGrid } from '../UpstackDataGrid/UpstackDataGrid';
import {
  advancedNumericOperators,
  advancedOperators,
  runPickerOperators,
  advancedDatePickerOperator
} from '../../Compounds/UpstackDataGrid/helpers';
import { Icon } from '../../Atoms/Icon';
import { TextSpan } from '../../Atoms/Text';
import { Correction } from '../../../store/Correction';
import { GridCustomColDef } from '../../types';
import { formatPrice, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../store/GeneralStore';
import { DetailViewCard } from '../CardWithTitle';
import { DetailCTARenderer } from '../UpstackDataGrid/CellRenderers';
import { hasAnalystOrHigherPermissions } from '../../../store/User';
import { NewCorrectionDialog } from './NewCorrectionDialog';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';

export const allCols: GridCustomColDef[] = [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.CORRECTION, id: params.row.id, data: params.row }),
          replace: false
        }}
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'paidInRunDate',
    headerName: 'Paid In',
    sortable: true,
    filterable: true,
    minWidth: 120,
    flex: 1,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => {
      const date = params.value as string;
      if (!date) return '';
      return getFormattedDate(date, 'MMM YY');
    },
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'correctsRunDate',
    headerName: 'Corrects',
    sortable: true,
    filterable: true,
    minWidth: 120,
    flex: 1,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => {
      const date = params.value as string;
      if (!date) return '';
      return getFormattedDate(date, 'MMM YY');
    },
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'revenueRecurringCorrection',
    headerName: 'Recurring',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams<Correction>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'revenueOneTimeCorrection',
    headerName: 'One-Time',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams<Correction>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    },
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'note',
    headerName: 'Note',
    sortable: true,
    filterable: true,
    minWidth: 200,
    flex: 2,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdBy',
    headerName: 'Added By',
    sortable: false,
    filterable: false,
    minWidth: 250,
    flex: 1.5,
    description: 'Drag to move column',
    valueGetter: (value: Correction['createdBy']) => {
      if (!value) return '';
      return `${value.firstName} ${value.lastName}`.trim() || value.email;
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'updatedBy',
    headerName: 'Modified By',
    sortable: false,
    filterable: false,
    minWidth: 250,
    flex: 1.5,
    description: 'Drag to move column',
    valueGetter: (value: Correction['updatedBy']) => {
      if (!value) return '';
      return `${value.firstName} ${value.lastName}`.trim() || value.email;
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdAt',
    headerName: 'Added At',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    valueFormatter: (value) => getFormattedDate(value),
    valueGetter: (value) => (value ? new Date(value) : ''),
    filterOperators: advancedDatePickerOperator()
  }
];

export interface CorrectionMiniTableParams {
  id: string;
  fetchFunction: (
    filters?: ApiAdvancedFilter,
    forceRefresh?: boolean
  ) => Promise<PaginatedResponse<Correction> | undefined | void>;
  dataSubject: () => BehaviorSubject<PaginatedResponse<Correction>>;
  loadingSubject: () => BehaviorSubject<boolean>;
  assetNumber: string;
  supplierName: string;
  commissionsStatus: string;
  type: 'asset' | 'provisional-asset';
}

export const CorrectionMiniTable = ({
  id,
  fetchFunction,
  dataSubject,
  loadingSubject,
  assetNumber,
  supplierName,
  commissionsStatus,
  type
}: CorrectionMiniTableParams) => {
  const [corrections, setCorrections] = useState<PaginatedResponse<Correction>>({} as PaginatedResponse<Correction>);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [isCorrectionNewFormOpen, setIsCorrectionNewFormOpen] = useState<boolean>(false);

  const fetchCorrections = async (filters?: ApiAdvancedFilter, loading?: boolean, forceRefresh?: boolean) => {
    fetchFunction(
      {
        page: 1,
        pageSize: 50,
        ...filters,
        filters: [
          ...(filters?.filters || []),
          {
            field: 'combinedAssetId',
            operator: 'equals',
            value: id
          }
        ]
      },
      forceRefresh
    );
  };

  useEffect(() => {
    const dataSub = dataSubject().subscribe((r) => {
      if (!tableLoading) setCorrections(r);
      setLoading(false);
    });

    const loadingSub = loadingSubject().subscribe((loading) => setTableLoading(loading));

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [tableLoading]);

  useEffect(() => {
    if (id) fetchCorrections();
  }, [id]);

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit'
  };

  const handleNewCorrection = () => {
    setIsCorrectionNewFormOpen(true);
  };

  const canAddCorrections = hasAnalystOrHigherPermissions();
  const filterKeys = allCols.filter((c) => c.filterable).map((c) => c.field);

  const cardActions = () =>
    canAddCorrections ? (
      <button
        className="flex items-center space-x-2"
        onClick={handleNewCorrection}
        data-cy="new-correction-button"
        data-testid="new-correction-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Correction
        </TextSpan>
      </button>
    ) : undefined;

  return (
    <DetailViewCard
      enableCollapse
      key="correction-table-card"
      title="Corrections"
      dataTestId="correction-view-card"
      className="mt-4"
      cardActions={cardActions()}>
      <Fragment>
        {loading && <ComponentSpinner label="Loading Correction Data" />}
        {!loading && (
          <UpstackDataGrid
            rows={corrections.data || []}
            sxStyles={sxStyle}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
              sorting: { sortModel: [{ field: 'paidInRunDate', sort: 'desc' }] }
            }}
            filterKeys={filterKeys}
            currentPage={corrections.page}
            rowCount={corrections.totalCount || 0}
            columns={allCols}
            dataCy="correction-mini-data-grid-pagination"
            fetchFunction={fetchCorrections}
            title="correction"
            showSearch={false}
            page="correctionGrid_MiniTable"
            apiFilter
            gridSettings={{ muiConfig: {}, disabledFields: [] }}
            pageSizeOptions={[50]}
            showToolbar={false}
            withoutSave
            localLoading={tableLoading}
            pagination={corrections.totalPages > 1}
            advancedFiltering
          />
        )}
        {canAddCorrections && (
          <NewCorrectionDialog
            open={isCorrectionNewFormOpen}
            onClose={(refresh: boolean) => {
              setIsCorrectionNewFormOpen(false);
              if (refresh) fetchCorrections(undefined, true, true);
            }}
            combinedAssetId={id}
            assetNumber={assetNumber}
            supplierName={supplierName}
            commissionsStatus={commissionsStatus}
            type={type}
          />
        )}
      </Fragment>
    </DetailViewCard>
  );
};
